import {
  ensureMunchkinReady,
  isValidEmail,
  normalizeLeadForHubspot,
  trackMunchkinPageVisit,
} from "./hubspotIntegration";

const MARKETO_FORMS_SCRIPT_SRC =
  "https://app-sjp.marketo.com/js/forms2/js/forms2.min.js";
const MARKETO_FORM_HOST_ID = "roi-marketo-form-host";
const HIDDEN_INTEGRATION_HOST_STYLE =
  "position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;";
const MARKETO_FORM_READY_TIMEOUT_MS = 20000;
const MARKETO_FORM_FIELD_KEYS = [
  "FirstName",
  "LastName",
  "Email",
  "Company",
  "Phone",
  "Title",
  "number_of_agents",
  "Receive_TCL_Communique__c",
];

let marketoFormRef = null;
let marketoFormReadyResolve = null;
let marketoInitPromise = null;
let marketoSubmitResolve = null;

let marketoFormReady = createMarketoFormReadyPromise();

function createMarketoFormReadyPromise() {
  return new Promise((resolve) => {
    marketoFormReadyResolve = resolve;
  });
}

function resetMarketoFormState() {
  marketoFormRef = null;
  marketoInitPromise = null;
  marketoSubmitResolve = null;
  marketoFormReady = createMarketoFormReadyPromise();
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

function getMarketoConfig() {
  const formId = Number(process.env.REACT_APP_MARKETO_FORM_ID || "7370");

  return {
    baseUrl: process.env.REACT_APP_MARKETO_BASE_URL || "https://app-sjp.marketo.com",
    munchkinId:
      process.env.REACT_APP_MARKETO_MUNCHKIN_ID ||
      process.env.REACT_APP_MUNCHKIN_ID ||
      "362-YWJ-377",
    formId,
    formElementId: `mktoForm_${formId}`,
    target: `#${MARKETO_FORM_HOST_ID}`,
    agentsField:
      process.env.REACT_APP_MARKETO_AGENTS_FIELD ||
      process.env.REACT_APP_HUBSPOT_AGENTS_FIELD ||
      "number_of_agents",
  };
}

export function isMarketoIntegrationEnabled() {
  const raw = String(process.env.REACT_APP_MARKETO_ENABLE || "").trim().toLowerCase();
  if (raw === "false" || raw === "0" || raw === "no") return false;
  if (raw === "true" || raw === "1" || raw === "yes") return true;
  return !!(
    process.env.REACT_APP_MARKETO_FORM_ID &&
    (process.env.REACT_APP_MARKETO_MUNCHKIN_ID || process.env.REACT_APP_MUNCHKIN_ID)
  );
}

function isMarketoFormConnected() {
  if (!marketoFormRef) return false;
  const formEl = document.getElementById(getMarketoConfig().formElementId);
  return !!(formEl && document.body.contains(formEl));
}

export function waitForMarketoForm(timeoutMs = MARKETO_FORM_READY_TIMEOUT_MS) {
  if (marketoFormRef && isMarketoFormConnected()) {
    return Promise.resolve(marketoFormRef);
  }

  return Promise.race([
    marketoFormReady,
    delay(timeoutMs).then(() => {
      throw new Error(
        `[ROI Marketo] Form not ready within ${timeoutMs}ms — check forms2.min.js is not blocked`
      );
    }),
  ]);
}

function ensureMarketoFormHost() {
  if (typeof document === "undefined") return null;

  const { formElementId } = getMarketoConfig();
  let host = document.getElementById(MARKETO_FORM_HOST_ID);

  if (!host) {
    host = document.createElement("div");
    host.id = MARKETO_FORM_HOST_ID;
    host.className = MARKETO_FORM_HOST_ID;
    host.setAttribute("aria-hidden", "true");
    host.style.cssText = HIDDEN_INTEGRATION_HOST_STYLE;
    document.body.appendChild(host);
  }

  ensureMarketoFormElement(host, formElementId);
  return host;
}

function ensureMarketoFormElement(host, formElementId) {
  let formEl = document.getElementById(formElementId);
  if (!formEl) {
    formEl = document.createElement("form");
    formEl.id = formElementId;
    host.appendChild(formEl);
  }
  return formEl;
}

export function initMarketoForm({
  baseUrl = "https://app-sjp.marketo.com",
  munchkinId = "362-YWJ-377",
  formId = 7370,
  target = `#${MARKETO_FORM_HOST_ID}`,
} = {}) {
  if (marketoFormRef && isMarketoFormConnected()) {
    return Promise.resolve(marketoFormRef);
  }

  if (marketoFormRef && !isMarketoFormConnected()) {
    resetMarketoFormState();
  }

  if (marketoInitPromise) return marketoInitPromise;

  marketoInitPromise = (async () => {
    const host = ensureMarketoFormHost() || document.querySelector(target);
    if (!host) {
      throw new Error(`[ROI Marketo] Host element not found: ${target}`);
    }

    await loadScript(MARKETO_FORMS_SCRIPT_SRC);

    if (!window.MktoForms2?.loadForm) {
      throw new Error("Marketo Forms 2 library is not available");
    }

    return new Promise((resolve, reject) => {
      let settled = false;

      const finish = (form) => {
        if (settled) return;
        settled = true;
        marketoFormRef = form;
        console.log("[ROI Marketo] Hidden form ready");
        logMarketoFormFieldNames(form);

        if (marketoFormReadyResolve) {
          marketoFormReadyResolve(form);
          marketoFormReadyResolve = null;
        }
        resolve(form);
      };

      const fail = (err) => {
        if (settled) return;
        settled = true;
        reject(err);
      };

      try {
        window.MktoForms2.loadForm(baseUrl, munchkinId, formId, (form) => {
          finish(form);
        });
      } catch (loadErr) {
        fail(loadErr);
        return;
      }

      setTimeout(() => {
        if (!settled) {
          fail(
            new Error(
              `[ROI Marketo] Form not ready within ${MARKETO_FORM_READY_TIMEOUT_MS}ms`
            )
          );
        }
      }, MARKETO_FORM_READY_TIMEOUT_MS);
    });
  })().catch((err) => {
    marketoInitPromise = null;
    throw err;
  });

  return marketoInitPromise;
}

export async function bootMarketoIntegration() {
  if (!isMarketoIntegrationEnabled()) {
    console.log(
      "[ROI Marketo] Integration disabled. REACT_APP_MARKETO_ENABLE=",
      process.env.REACT_APP_MARKETO_ENABLE
    );
    return false;
  }

  const host = ensureMarketoFormHost();
  if (!host) {
    console.warn("[ROI Marketo] Unable to create hidden form host");
    return false;
  }

  const { baseUrl, munchkinId, formId, target } = getMarketoConfig();

  try {
    await ensureMunchkinReady(munchkinId);
    await initMarketoForm({ baseUrl, munchkinId, formId, target });
    console.log("[ROI Marketo] Integration ready");
    return true;
  } catch (err) {
    console.warn("[ROI Marketo] boot failed:", err);
    return false;
  }
}

function normalizeLeadForMarketo(leadData = {}) {
  const lead = normalizeLeadForHubspot(leadData || {});
  const agentsValue = lead.number_of_agents || lead.no_of_agents;

  const vals = {
    FirstName: lead.firstname || "-",
    LastName: lead.lastname || "-",
    Email: String(lead.email || "").trim(),
    Company: lead.company || "-",
    Phone: lead.phone || "-",
    Title: lead.jobtitle || "-",
  };

  if (agentsValue) {
    vals.number_of_agents = String(agentsValue).trim();
  }

  const consent = lead.receive_tcl_communique__c;
  const consentYes =
    consent === undefined ||
    consent === null ||
    consent === "" ||
    consent === true ||
    consent === "true" ||
    consent === 1 ||
    consent === "1";
  vals.Receive_TCL_Communique__c = consentYes ? "yes" : "no";

  return vals;
}

function pickMarketoFormVals(vals = {}) {
  const picked = {};

  MARKETO_FORM_FIELD_KEYS.forEach((key) => {
    if (vals[key] != null && String(vals[key]).trim() !== "") {
      picked[key] = String(vals[key]).trim();
    }
  });

  return picked;
}

function getMarketoFormElement(form) {
  if (!form || typeof form.getFormElem !== "function") return null;
  const formEl = form.getFormElem();
  return formEl?.find ? formEl : null;
}

function logMarketoFormFieldNames(form) {
  const formEl = getMarketoFormElement(form);
  if (!formEl) return;

  const names = formEl
    .find("input, select, textarea")
    .toArray()
    .map((el) => el.name)
    .filter(Boolean);

  console.log("[ROI Marketo] Available form field names:", [...new Set(names)]);
}

function logMarketoValidationErrors(form) {
  const formEl = getMarketoFormElement(form);
  if (!formEl) return;

  const errors = formEl
    .find(".mktoError, .mktoErrorMsg")
    .toArray()
    .map((el) => String(el.textContent || "").trim())
    .filter(Boolean);

  if (errors.length) {
    console.warn("[ROI Marketo] Validation errors on form:", errors);
  }
}

function setMarketoDomFieldValues(form, vals = {}) {
  const formEl = getMarketoFormElement(form);
  if (!formEl) return;

  Object.entries(vals).forEach(([key, value]) => {
    const strVal = String(value ?? "");

    if (key === "Receive_TCL_Communique__c") {
      const wantYes = /^(yes|true|1)$/i.test(strVal);
      formEl.find('input[name="Receive_TCL_Communique__c"]').each(function setConsent() {
        if (this.type !== "checkbox") return;
        if (!wantYes) {
          this.checked = false;
          return;
        }
        const optionVal = String(this.value || "yes").toLowerCase();
        if (!this.value || optionVal === "yes" || optionVal === "true" || optionVal === "1") {
          this.checked = true;
        }
      });
      return;
    }

    formEl
      .find(`input[name="${key}"], select[name="${key}"], textarea[name="${key}"]`)
      .each(function setFieldValue() {
        if (this.type === "checkbox" || this.type === "radio") return;
        this.value = strVal;
      });
  });
}

function registerMarketoSuccessHandler(form) {
  if (!form || typeof form.onSuccess !== "function") return;

  form.onSuccess((values, followUpUrl) => {
    console.log("[ROI Marketo] onSuccess callback fired", { values, followUpUrl });
    trackMunchkinPageVisit();
    if (marketoSubmitResolve) {
      marketoSubmitResolve({ source: "onSuccess", ok: true });
      marketoSubmitResolve = null;
    }
    return false;
  });
}

function watchMarketoLeadCaptureRequest(timeoutMs = 20000) {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (ok) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ source: "network", ok: !!ok });
    };

    const isCaptureUrl = (url) => /leadCapture\/save2/i.test(String(url || ""));

    const origXhrOpen = XMLHttpRequest.prototype.open;
    const origXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function patchedXhrOpen(method, url, ...rest) {
      this.__mktoCaptureUrl = url;
      return origXhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function patchedXhrSend(...args) {
      if (isCaptureUrl(this.__mktoCaptureUrl)) {
        this.addEventListener("load", () => {
          finish(this.status >= 200 && this.status < 400);
        });
        this.addEventListener("error", () => finish(false));
      }
      return origXhrSend.apply(this, args);
    };

    const jq = window.jQuery || window.$;
    const origAjax = jq?.ajax;

    if (origAjax) {
      jq.ajax = function patchedAjax(url, options) {
        const opts = typeof url === "object" ? url : options || {};
        const ajaxUrl = typeof url === "string" ? url : opts.url;
        const jqXHR = origAjax.apply(this, arguments);

        if (isCaptureUrl(ajaxUrl)) {
          jqXHR.done(() => finish(true));
          jqXHR.fail(() => finish(false));
        }

        return jqXHR;
      };
    }

    const timer = setTimeout(() => finish(false), timeoutMs);

    function cleanup() {
      clearTimeout(timer);
      XMLHttpRequest.prototype.open = origXhrOpen;
      XMLHttpRequest.prototype.send = origXhrSend;
      if (jq && origAjax) {
        jq.ajax = origAjax;
      }
    }
  });
}

function populateMarketoForm(form, vals = {}) {
  const marketoVals = pickMarketoFormVals(vals);

  if (typeof form.vals === "function") {
    form.vals(marketoVals);
  }

  setMarketoDomFieldValues(form, marketoVals);

  if (typeof form.vals === "function") {
    console.log("[ROI Marketo] Read back form values:", form.vals());
  }

  return marketoVals;
}

function triggerMarketoFormSubmit(form) {
  const formEl = getMarketoFormElement(form);
  const submitBtn =
    formEl?.find(".mktoButton")?.get?.(0) ||
    formEl?.find("button[type='submit']")?.get?.(0);

  if (submitBtn) {
    submitBtn.click();
    return true;
  }

  if (typeof form.submit === "function") {
    form.submit();
    return true;
  }

  console.warn("[ROI Marketo] Submit control not found on Marketo form");
  return false;
}

async function submitMarketoForm(form) {
  registerMarketoSuccessHandler(form);

  const onSuccessPromise = new Promise((resolve) => {
    marketoSubmitResolve = resolve;
    setTimeout(() => {
      if (marketoSubmitResolve === resolve) {
        marketoSubmitResolve = null;
        resolve({ source: "timeout", ok: false });
      }
    }, 20000);
  });

  const networkPromise = watchMarketoLeadCaptureRequest(20000);

  await delay(300);

  const didSubmit = triggerMarketoFormSubmit(form);
  if (!didSubmit) {
    return { source: "submit", ok: false };
  }

  const [successResult, networkResult] = await Promise.allSettled([
    onSuccessPromise,
    networkPromise,
  ]);

  const onSuccessOk =
    successResult.status === "fulfilled" && successResult.value?.ok === true;
  const networkOk =
    networkResult.status === "fulfilled" && networkResult.value?.ok === true;
  const confirmed = onSuccessOk || networkOk;

  if (!confirmed) {
    logMarketoValidationErrors(form);
    if (!onSuccessOk) {
      console.warn("[ROI Marketo] onSuccess did not confirm submit");
    }
    if (!networkOk) {
      console.warn("[ROI Marketo] leadCapture/save2 request did not succeed");
    }
  }

  const source = networkOk
    ? networkResult.value.source
    : onSuccessOk
      ? successResult.value.source
      : "none";

  return { source, ok: confirmed };
}

export async function submitLeadToMarketo(leadData = {}) {
  if (!isMarketoIntegrationEnabled()) {
    console.warn("[ROI Marketo] Integration disabled");
    return false;
  }

  const vals = normalizeLeadForMarketo(leadData || {});
  const email = String(vals.Email || "").trim();

  if (!isValidEmail(email)) {
    console.warn("[ROI Marketo] Invalid or missing email in lead data:", vals.Email);
    return false;
  }

  vals.Email = email;
  console.log("[ROI Marketo] Submitting normalized lead:", vals);

  if (!isMarketoFormConnected()) {
    resetMarketoFormState();
    await bootMarketoIntegration();
  }

  let form = marketoFormRef;

  if (!form) {
    try {
      form = await waitForMarketoForm();
    } catch (waitErr) {
      console.warn(waitErr.message);
      const { baseUrl, munchkinId, formId, target } = getMarketoConfig();
      await initMarketoForm({ baseUrl, munchkinId, formId, target });
      form = await waitForMarketoForm();
    }
  }

  try {
    const marketoVals = populateMarketoForm(form, vals);
    console.log("[ROI Marketo] Form values set:", marketoVals);

    const submitResult = await submitMarketoForm(form);
    const confirmed = !!submitResult?.ok;
    console.log("[ROI Marketo] Submit confirmed:", confirmed, submitResult);

    if (!confirmed) {
      console.warn(
        "[ROI Marketo] Submit was not confirmed — check Network for leadCapture/save2 and validation errors above"
      );
      return false;
    }

    return true;
  } catch (err) {
    console.warn("[ROI Marketo] submit failed:", err);
    return false;
  }
}
